// Sessões ativas do próprio usuário (REQ-AUTH-029): lista com a sessão corrente marcada; encerrar exige step-up —
// `useStepUpAction` intercepta o 401, abre o diálogo e repete a revogação (no lugar ou após o retorno do provedor).
import { Badge, Button, Skeleton } from '@giyus/ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProblemAlert } from '../../../components';
import { useStepUpAction } from '../../../lib/auth/useStepUp';
import { useDateFormatter } from '../../../lib/dates/useDateFormatter';
import { useMySessions, useRevokeMySession } from '../api/accountQueries';

export const REVOKE_SESSION_ACTION = 'account.revoke-session';

export function SessionsCard() {
  const { t } = useTranslation('identity');
  const dates = useDateFormatter();
  const { data, isLoading, isError, error, refetch } = useMySessions();
  const revoke = useRevokeMySession();
  const [done, setDone] = useState(false);
  const run = useCallback(
    async (sessionId: string) => {
      setDone(false);
      await revoke.mutateAsync(sessionId);
      setDone(true);
    },
    [revoke],
  );
  const action = useStepUpAction<string>({
    key: REVOKE_SESSION_ACTION,
    run,
    toPayload: (sessionId) => sessionId,
    fromPayload: (payload) => (typeof payload === 'string' ? payload : null),
  });

  return (
    <section className="flex flex-col gap-3 rounded-card border p-4" data-testid="account-sessions">
      <div>
        <h2 className="text-body font-semibold">{t('account.sessions.title')}</h2>
        <p className="text-body text-muted-foreground">{t('account.sessions.subtitle')}</p>
      </div>
      {action.resumedWithoutPayload && (
        <p role="status" className="text-body text-muted-foreground">
          {t('account.sessions.resumed')}
        </p>
      )}
      {done && (
        <p role="status" className="text-body text-muted-foreground" data-testid="session-revoked">
          {t('account.sessions.revoked')}
        </p>
      )}
      {revoke.isError && !action.resumedWithoutPayload && <ProblemAlert error={revoke.error} />}
      {isError ? (
        <ProblemAlert
          error={error}
          onRetry={() => {
            void refetch();
          }}
        />
      ) : isLoading || !data ? (
        <Skeleton className="h-24 w-full" />
      ) : data.length === 0 ? (
        <p className="text-body text-muted-foreground">{t('account.sessions.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {data.map((session) => (
            <li
              key={session.id}
              className="flex flex-col gap-2 rounded-card border p-3 sm:flex-row sm:items-center sm:justify-between"
              data-testid={`session-${session.id}`}
            >
              <div className="flex flex-col gap-1 text-body">
                <span className="flex items-center gap-2 font-medium">
                  {session.deviceLabel ?? t('account.sessions.unknownDevice')}
                  {session.isCurrent && (
                    <Badge variant="secondary">{t('account.sessions.current')}</Badge>
                  )}
                </span>
                <span className="text-caption text-muted-foreground">
                  {t('account.sessions.ip')}: {session.ipAddress ?? '—'} ·{' '}
                  {t('account.sessions.authTime')}: {dates.dateTime(session.authTime)} ·{' '}
                  {t('account.sessions.lastActivity')}: {dates.dateTime(session.lastActivityAt)}
                </span>
                <span className="text-caption text-muted-foreground">
                  {t('account.sessions.idleExpires')}: {dates.dateTime(session.idleExpiresAt)} ·{' '}
                  {t('account.sessions.absoluteExpires')}:{' '}
                  {dates.dateTime(session.absoluteExpiresAt)}
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={revoke.isPending}
                data-testid={`revoke-session-${session.id}`}
                onClick={() => {
                  action.execute(session.id).catch(() => {
                    // erro exibido pelo ProblemAlert (revoke.error)
                  });
                }}
              >
                {t('account.sessions.revoke')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
